'use client';

import { useForkRef } from '@mui/material/utils';
import { animate } from 'motion';
import { cloneElement, forwardRef, isValidElement, useEffect, useRef, useState } from 'react';
const ENTER_OPTIONS = {
  duration: 0.22,
  ease: [0.22, 1, 0.36, 1]
};
const EXIT_OPTIONS = {
  duration: 0.18,
  ease: [0.4, 0, 0.2, 1]
};

// Keys consumed by the transition contract itself. Everything else Modal/Dialog put on the
// transition element (e.g. `tabIndex`, `role`, `aria-*`) is meant for the paper and is forwarded —
// same as `restChildProps` in MUI's Fade.
const TRANSITION_ONLY_PROPS = new Set(['timeout', 'addEndListener', 'mountOnEnter', 'unmountOnExit', 'easing', 'enter', 'exit', 'nodeRef', 'TransitionComponent']);

/**
 * Bridges MUI Dialog's transition contract (`in`, `onEnter`/`onExited`, mount timing)
 * onto motion's imperative `animate`: fade + slide on the paper element.
 * The backdrop keeps MUI's default fade.
 */
export const ConfirmDialogTransition = forwardRef(function ConfirmDialogTransition({
  appear = true,
  children,
  in: inProp,
  onEnter,
  onEntering,
  onEntered,
  onExit,
  onExiting,
  onExited,
  style,
  ...rest
}, ref) {
  const nodeRef = useRef(null);
  const child = isValidElement(children) ? children : null;
  const handleRef = useForkRef(nodeRef, child?.props.ref, ref);
  const controlsRef = useRef(null);
  const isInitialMountRef = useRef(true);
  const [exited, setExited] = useState(!inProp);
  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    const isInitialMount = isInitialMountRef.current;
    isInitialMountRef.current = false;
    controlsRef.current?.stop();
    if (inProp) {
      setExited(false);
      if (isInitialMount && !appear) {
        node.style.opacity = '1';
        node.style.transform = 'none';
      } else {
        onEnter?.(node, isInitialMount);
        onEntering?.(node, isInitialMount);
        controlsRef.current = animate(node, {
          opacity: 1,
          y: 0,
          scale: 1
        }, ENTER_OPTIONS);
        controlsRef.current.then(() => onEntered?.(node, isInitialMount));
      }
    } else if (!isInitialMount) {
      onExit?.(node);
      onExiting?.(node);
      controlsRef.current = animate(node, {
        opacity: 0,
        y: 16,
        scale: 0.95
      }, EXIT_OPTIONS);
      controlsRef.current.then(() => {
        onExited?.(node);
        setExited(true);
      });
    }
    return () => controlsRef.current?.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProp]);
  if (!child) return null;
  const childProps = Object.fromEntries(Object.entries(rest).filter(([key]) => !TRANSITION_ONLY_PROPS.has(key)));
  return cloneElement(child, {
    ...childProps,
    ref: handleRef,
    style: {
      opacity: 0,
      transform: 'translateY(24px)',
      visibility: !inProp && exited ? 'hidden' : undefined,
      ...style,
      ...child.props.style
    }
  });
});